import { FC, useCallback, useMemo } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import { isArray } from "lodash";
import { FormHelperText } from "@mui/material";
import {
  DateRangeCalendar,
  DateRangePickerDayProps,
} from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";
import { transformAbsencePlanToPlannedDays } from "src/utils/Company/Absences/transformAbsencePlan";
import { Loader } from "src/Components/Loader/Loader";
import CustomDateRangePickersDay from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/CustomDateRangePickersDay";
import {
  IAbsencePlan,
  IAbsenceRequestDay,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { datepickerStyles } from "./Datepicker.styles";

interface IAbsenceRequestDatepickerProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  absencePlanData?: IAbsencePlan;
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  loading: boolean;
  disableMonthChange?: boolean;
  handleMonthChange: (month: Date) => void;
  restrictToMonths?: { start: Date; end: Date };
}

const AbsenceRequestDatepicker: FC<IAbsenceRequestDatepickerProps> = ({
  absenceRequestDays,
  excludedDays,
  absencePlanData,
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  loading,
  disableMonthChange = false,
  handleMonthChange,
  restrictToMonths,
}) => {
  const { control } = useFormContext();

  const plannedDays = useMemo(() => {
    if (!absencePlanData) return [];

    return transformAbsencePlanToPlannedDays(absencePlanData);
  }, [absencePlanData]);

  const dayRenderer = useCallback(
    (props: DateRangePickerDayProps) => (
      <CustomDateRangePickersDay
        absenceRequestDays={absenceRequestDays}
        excludedDays={excludedDays}
        plannedDays={plannedDays}
        multipleRequestsPerDayAllowed={multipleRequestsPerDayAllowed}
        billInCalendarDays={billInCalendarDays}
        {...props}
      />
    ),
    [
      absenceRequestDays,
      excludedDays,
      plannedDays,
      multipleRequestsPerDayAllowed,
      billInCalendarDays,
    ]
  );

  return (
    <Controller
      control={control}
      name="dates"
      render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
        <>
          <DateRangeCalendar
            ref={ref}
            calendars={2}
            value={value}
            loading={loading}
            disableAutoMonthSwitching
            autoFocus={false}
            onChange={onChange}
            dayOfWeekFormatter={(date: Date) =>
              date.toLocaleString("pl", { weekday: "short" })
            }
            renderLoading={() => <Loader />}
            slots={{
              day: dayRenderer,
            }}
            sx={{
              ...datepickerStyles,
              ...(disableMonthChange && {
                ".MuiPickersArrowSwitcher-button": {
                  visibility: "hidden",
                },
              }),
            }}
            data-testid="absence-request-form-field__date-range"
          />
          <FormHelperText error={!!error}>
            {isArray(error)
              ? (error as unknown as FieldError[])?.map((item) => (
                  <>{__(item.message!)}</>
                ))
              : error?.message && __(error.message)}
          </FormHelperText>
        </>
      )}
    />
  );
};

export default AbsenceRequestDatepicker;
